/**
 * Umrechnung eines {@link DrumPattern} in eine chronologisch sortierte Liste
 * von Audio-Ereignissen.
 *
 * Rein funktional: keine Abhängigkeit von AudioContext, DOM oder
 * Browser-APIs. Damit ist die zeitliche Planung vollständig unit-testbar.
 */

import { DrumPattern } from '../model/DrumPattern';
import { INSTRUMENT_ORDER, type DrumInstrument } from '../model/types';
import { dynamicForHitType, gainForDynamic, type Dynamic } from './dynamics';
import { stepDurationSeconds, stepStartSeconds } from './timing';

/** Ein zeitlich geplanter Wiedergabe-Anschlag. */
export interface AudioEvent {
  readonly instrument: DrumInstrument;
  /** Rasterschritt (0..15) des Anschlags. */
  readonly step: number;
  /** Startzeit relativ zum Patternanfang, in Sekunden. */
  readonly time: number;
  /** Dauer eines Rasterschritts in Sekunden (Rasterinformation). */
  readonly duration: number;
  /** Dynamik des Schlags. */
  readonly dynamic: Dynamic;
  /** Aus der Dynamik abgeleiteter, begrenzter Lautstärkefaktor. */
  readonly gain: number;
}

const instrumentRank = new Map<DrumInstrument, number>(
  INSTRUMENT_ORDER.map((instrument, index) => [instrument, index]),
);

/**
 * Erzeugt die chronologisch sortierten Audio-Ereignisse eines Patterns.
 *
 * Gleichzeitige Schläge verschiedener Instrumente erhalten dieselbe
 * Startzeit; die Sekundärsortierung folgt der Instrumentreihenfolge, sodass
 * das Ergebnis deterministisch ist.
 */
export function scheduleAudioEvents(
  pattern: DrumPattern,
  bpm: number,
): AudioEvent[] {
  const stepDuration = stepDurationSeconds(bpm);
  const events: AudioEvent[] = [];

  for (const instrument of INSTRUMENT_ORDER) {
    for (const step of pattern.steps) {
      const hit = pattern.getHit(instrument, step);
      if (!hit) {
        continue;
      }
      const dynamic = dynamicForHitType(hit.type);
      events.push({
        instrument,
        step,
        time: stepStartSeconds(step, bpm),
        duration:
          (stepDuration * 4) / pattern.subdivisions[Math.floor(step / 4)]!,
        dynamic,
        gain: gainForDynamic(dynamic),
      });
    }
  }

  events.sort((a, b) => {
    if (a.time !== b.time) {
      return a.time - b.time;
    }
    return (
      (instrumentRank.get(a.instrument) ?? 0) -
      (instrumentRank.get(b.instrument) ?? 0)
    );
  });

  return events;
}
